import React, { useCallback } from 'react';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

const errorMessage = (error) => error?.response?.data?.message || $t('Something went wrong');

function ApproverRules({ rules }) {
    return (
        <div className="tw-space-y-2">
            <div>{$t('This user is an approver on these approval rules. They stay on the rules while deactivated, so each rule should be updated:')}</div>
            <ul className="tw-mb-0 tw-list-disc tw-ps-5 tw-text-sm">
                {rules.map((rule) => (
                    <li key={`${rule.kind}-${rule.approval_settings_id}`}>
                        {/* The trigger arrives already translated by the server. */}
                        {rule.event_name} › {rule.trigger || $t('Approval')}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * The member actions shared by the table and the panel. Each one confirms
 * first, then reports the refreshed member to `onDone`.
 */
export default function useOrganisationUserActions({ usersUrl, onDone }) {
    const confirm = useConfirmationModal();
    const toast = useToast();

    const post = useCallback(
        (user, action, successMessage) =>
            axios
                .post(`${usersUrl}/${user.id}/${action}`)
                .then(({ data }) => {
                    toast.success(successMessage);
                    onDone(data.data);
                })
                .catch((error) => toast.error(errorMessage(error))),
        [usersUrl, onDone, toast],
    );

    const deactivate = useCallback(
        (user) =>
            axios
                .get(`${usersUrl}/${user.id}/approver-rules`)
                .then(({ data }) =>
                    confirm({
                        title: $t('Deactivate {{name}}', { name: user.name }),
                        message:
                            data.data.length > 0 ? (
                                <ApproverRules rules={data.data} />
                            ) : (
                                $t('{{name}} will lose access to this organisation and its projects from their next request.', { name: user.name })
                            ),
                        warning: $t('Their memberships and roles are kept, so reactivating them restores access.'),
                        onConfirm: () => post(user, 'deactivate', $t('The user was deactivated')),
                    }),
                )
                .catch((error) => toast.error(errorMessage(error))),
        [usersUrl, confirm, post, toast],
    );

    const reactivate = useCallback(
        (user) =>
            confirm({
                title: $t('Reactivate {{name}}', { name: user.name }),
                message: $t('{{name}} will regain access to this organisation and its projects.', { name: user.name }),
                onConfirm: () => post(user, 'reactivate', $t('The user was reactivated')),
            }),
        [confirm, post],
    );

    const resendInvite = useCallback(
        (user) =>
            confirm({
                title: $t('Resend invite'),
                message: $t('Send the invitation to {{email}} again?', { email: user.email }),
                onConfirm: () => post(user, 'invite', $t('The invitation was sent')),
            }),
        [confirm, post],
    );

    const resetMfa = useCallback(
        (user) =>
            confirm({
                title: $t('Reset MFA for {{name}}', { name: user.name }),
                message: $t('Their MFA set-up is removed and they set it up again at their next login.'),
                warning: $t(
                    'This affects every organisation they belong to and signs them out everywhere. Access tokens issued to integrations are not revoked.',
                ),
                onConfirm: () => post(user, 'mfa-reset', $t('MFA was reset')),
            }),
        [confirm, post],
    );

    return { deactivate, reactivate, resendInvite, resetMfa };
}
